import { useState } from 'react';

const REVIEW_FORM_URL = 'https://readdy.ai/api/form/dahcdrjoh653ivfvnr9g';

export default function ReviewForm() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [success, setSuccess] = useState(false);

  function close() {
    setOpen(false);
    setStatus('idle');
    setErrorMessage('');
  }

  function closeSuccess() {
    setSuccess(false);
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;

    const guard = form.elements.namedItem('website_alt') as HTMLInputElement | null;
    if (guard && guard.value.trim() !== '') {
      setSuccess(true);
      return;
    }

    setStatus('sending');
    setErrorMessage('');

    try {
      const formData = new FormData(form);
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key === 'website_alt') return;
        payload.append(key, String(value));
      });

      const response = await fetch(REVIEW_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setStatus('idle');
        form.reset();
        setOpen(false);
        setSuccess(true);
      } else {
        setStatus('error');
        setErrorMessage(serverMessage || 'Не удалось отправить отзыв. Попробуйте ещё раз.');
      }
    } catch {
      setStatus('error');
      setErrorMessage('Произошла ошибка при отправке. Проверьте соединение и попробуйте снова.');
    }
  }

  return (
    <>
      <div className="mt-12 flex justify-center">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="whitespace-nowrap inline-flex items-center gap-2 rounded-md px-8 py-3.5 font-heading text-sm font-semibold bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-chat-3-line"></i>
          Оставить отзыв
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={close}
          ></div>

          <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-8">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-heading text-xl font-bold text-foreground-950">Оставить отзыв</h3>
                <p className="mt-1 text-sm text-foreground-600">Поделитесь результатом — это помогает другим клиентам.</p>
              </div>
              <button
                type="button"
                onClick={close}
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:text-foreground-950 hover:bg-background-200/70 transition-colors cursor-pointer"
                aria-label="Закрыть"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <form data-readdy-form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
              <div>
                <label htmlFor="review-name" className="block text-sm text-foreground-700 mb-1.5">
                  Как вас зовут <span className="text-primary-400">*</span>
                </label>
                <input
                  id="review-name"
                  name="name"
                  type="text"
                  required
                  placeholder="Иван Петров"
                  className="w-full rounded-md border border-background-300/60 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400/70"
                />
              </div>

              <div>
                <label htmlFor="review-site-name" className="block text-sm text-foreground-700 mb-1.5">
                  Что за сайт / сообщество <span className="text-primary-400">*</span>
                </label>
                <input
                  id="review-site-name"
                  name="site_name"
                  type="text"
                  required
                  placeholder="Название сайта или сообщества"
                  className="w-full rounded-md border border-background-300/60 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400/70"
                />
              </div>

              <div>
                <label htmlFor="review-site-link" className="block text-sm text-foreground-700 mb-1.5">
                  Ссылка на сайт / сообщество <span className="text-primary-400">*</span>
                </label>
                <input
                  id="review-site-link"
                  name="site_link"
                  type="url"
                  required
                  placeholder="https://example.com"
                  className="w-full rounded-md border border-background-300/60 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400/70"
                />
              </div>

              <div>
                <label htmlFor="review-manager" className="block text-sm text-foreground-700 mb-1.5">
                  Менеджер, с которым вы работали
                </label>
                <input
                  id="review-manager"
                  name="manager"
                  type="text"
                  placeholder="Имя менеджера"
                  className="w-full rounded-md border border-background-300/60 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400/70"
                />
              </div>

              <div>
                <label htmlFor="review-text" className="block text-sm text-foreground-700 mb-1.5">
                  Отзыв <span className="text-primary-400">*</span>
                </label>
                <textarea
                  id="review-text"
                  name="text"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="Расскажите, как изменились подписчики, охваты и заявки..."
                  className="w-full rounded-md border border-background-300/60 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400/70 resize-none"
                ></textarea>
                <p className="mt-1 text-xs text-foreground-500">Максимум 500 символов</p>
              </div>

              <input
                name="website_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="field-guard-offscreen"
              />

              {status === 'error' && (
                <p className="text-sm text-red-500">{errorMessage}</p>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                className="whitespace-nowrap w-full inline-flex items-center justify-center gap-2 rounded-md px-6 py-3 font-heading text-sm font-semibold bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                {status === 'sending' ? 'Отправка...' : 'Отправить отзыв'}
              </button>
            </form>
          </div>
        </div>
      )}

      {success && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeSuccess}
          ></div>

          <div className="relative w-full max-w-md rounded-lg bg-background-100 border border-background-200/70 p-8 text-center">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-500/15 text-primary-300">
              <i className="ri-check-line text-2xl"></i>
            </div>
            <h3 className="mt-5 font-heading text-xl font-bold text-foreground-950">
              Ваш отзыв передан на модерацию
            </h3>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              В течение 24 часов мы проверим достоверность, и публикация будет в течение 24 часов с момента модерации.
            </p>
            <button
              type="button"
              onClick={closeSuccess}
              className="whitespace-nowrap mt-6 w-full inline-flex items-center justify-center gap-2 rounded-md px-6 py-3 font-heading text-sm font-semibold bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              Понятно
            </button>
          </div>
        </div>
      )}
    </>
  );
}